import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';

interface PasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
}

export const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({ 
  open, 
  onClose 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const handleRequestPasswordChange = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await apiClient.post('/api/user/request-password-change');
      setSuccess(true);
    } catch (err: any) {
      console.error('Password change request error:', err);
      setError(extractErrorMessage(err, 'Failed to send password change email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Change Password
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <EmailIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We've sent you an email with a secure link to change your password. 
              Please check your inbox and follow the instructions.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" paragraph>
              For security reasons, we'll send you an email with a secure link to change your password.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After clicking "Send Email", check your inbox for the password reset link.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {success ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestPasswordChange} 
              variant="contained" 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Sending...' : 'Send Email'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};